import {
  Avatar,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import { SeeAs, useAdminModuleMockedUser } from '@sodexo/admin-module-react';
import {
  ChevronDown,
  Feedback,
  filterSdxIconToWhite,
  QueryQueue,
  SdxColors,
  SecondaryColors,
} from '@sodexo/ui-components';
import type React from 'react';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { msalInstance } from '../../../msalInstance';
import theme from '../../../theme';

export function UserMenu() {
  const { t } = useTranslation('translation', { keyPrefix: 'components.topBar.userMenu' });
  const account = msalInstance.getActiveAccount();
  const navigate = useNavigate();
  const adminModuleMockedUser = useAdminModuleMockedUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const avatarLetters = useMemo(
    () =>
      (adminModuleMockedUser || account?.name)
        ?.replaceAll('_', '')
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase(),
    [account?.name, adminModuleMockedUser],
  );

  const openMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const openFormLink = () => {
    window
      .open(
        'https://forms.office.com/Pages/ResponsePage.aspx?id=wtaJoh87xEuPoGhm_zAAUizPyNeWHv5NsDEh2_vL3sVUMVJOUFFUS1VGMVVRSjVXWUxTVUNIVEZEVSQlQCN0PWcu',
        '_blank',
      )
      ?.focus();
  };
  const goToBatchAnalysis = () => navigate('/batch/list');

  return (
    <>
      <ButtonBase disableRipple type='button' data-testid='user-menu-button'>
        <Stack flexDirection='row' gap={1} alignItems='center' sx={{ cursor: 'pointer' }} onClick={openMenu}>
          <Avatar sx={{ backgroundColor: SecondaryColors.bronze, width: 32, height: 32, fontSize: 16 }}>
            {avatarLetters}
          </Avatar>
          <Stack justifyContent='center'>
            <Typography
              color={SdxColors.white}
              sx={{ textDecoration: adminModuleMockedUser ? 'line-through' : 'none' }}
              variant='subtitle2'
            >
              {account?.name}
            </Typography>
            <Typography sx={{ fontStyle: 'italic', fontSize: 10 }} color={SdxColors.white} variant='caption'>
              {adminModuleMockedUser || account?.username}
            </Typography>
          </Stack>
          <ChevronDown style={{ ...filterSdxIconToWhite }} width={20} />
        </Stack>
      </ButtonBase>

      <ThemeProvider theme={MenuTheme}>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          onClick={closeMenu}
          slotProps={{
            paper: {
              sx: {
                width: Math.max(anchorEl?.offsetWidth || 0, 200),
              },
            },
          }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <MenuItem onClick={goToBatchAnalysis} data-testid='user-menu-batch-analysis-button'>
            <ListItemIcon>
              <QueryQueue />
            </ListItemIcon>
            <ListItemText>{t('batchAnalysis')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={openFormLink}>
            <ListItemIcon>
              <Feedback />
            </ListItemIcon>
            <ListItemText>{t('contactUs')}</ListItemText>
          </MenuItem>

          <Suspense fallback={null}>
            <MenuItemSeeAs
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <SeeAs history={history}>
                <Typography variant='body2'>{t('seeAs')}</Typography>
              </SeeAs>
            </MenuItemSeeAs>
          </Suspense>
        </Menu>
      </ThemeProvider>
    </>
  );
}

const MenuItemSeeAs = styled(MenuItem)(({ theme }) => ({
  button: {
    width: '100%',
    margin: 0,
  },
}));

const MenuTheme = createTheme(theme, {
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          marginTop: theme.spacing(2),
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1),
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:nth-of-type(n)': {
            backgroundColor: 'transparent',
            borderRadius: theme.spacing(1),
            '&.Mui-focusVisible': {
              backgroundColor: SecondaryColors.bronzeLight,
            },
            '&:hover': {
              backgroundColor: SecondaryColors.bronzeLightest,
            },
          },
        },
      },
    },
  },
});
